import React, { useEffect, useState } from 'react';
import { Button, ButtonRow, Modal, Spinner } from '@myob/myob-widgets';
import { IntermediatePaymentCard, ValidatedPaymentCard } from 'modules/PaymentProfile/type';
import CreditCardForm from 'modules/PaymentProfile/components/EditCreditCardForm';
import { CustomisedModalBody } from 'modules/PaymentProfile/styles';
import * as telemetry from 'telemetry';
import { ETelemetryActionsAndLabels, ETelemetryNames } from 'telemetry/type';
import DirectDebitCardForm from '../EditDirectDebitCardForm';

type ModalProps = {
    isProcessing?: boolean;
    cancelModal: () => void;
    onSaveValidData: (data: ValidatedPaymentCard) => void;
};

export enum FormType {
    CreditCard = 'CreditCard',
    DirectDebit = 'DirectDebit',
    None = 'None',
}

export enum Progress {
    Open = 'open',
    Save = 'save',
    Cancel = 'cancel',
    Abandon = 'abandon',
}

function track(progress: Progress, activeForm: FormType) {
    telemetry.track(ETelemetryNames.PaymentProfile, {
        action: ETelemetryActionsAndLabels.Edit,
        label: ETelemetryActionsAndLabels.Edit,
        editData: { progress, selectedPaymentMethod: activeForm },
    });
}

const formDataIsValid = (data: IntermediatePaymentCard, activeFormType: FormType): boolean => {
    switch (activeFormType) {
        case FormType.CreditCard:
            return data.detail != null;
        case FormType.DirectDebit:
            return !!data.termsOfUseChecked && data.detail != null;
        default:
            return false;
    }
};

const EditModal: React.FC<ModalProps> = ({ cancelModal, onSaveValidData, isProcessing }) => {
    const [formData, setFormData] = useState<IntermediatePaymentCard>(new IntermediatePaymentCard());
    const [formIsValid, setFormIsValid] = useState(false);
    const [activeFormType, setActiveForm] = useState(FormType.None);
    const [emittedTrackStart, setEmittedTrackStart] = useState(false);

    const cancelModalWithTracking = () => {
        track(formData.detail == null ? Progress.Cancel : Progress.Abandon, activeFormType);
        cancelModal();
    };

    const onClickSaveBtn = () => {
        track(Progress.Save, activeFormType);
        onSaveValidData(formData as ValidatedPaymentCard);
    };
    if (!emittedTrackStart) {
        track(Progress.Open, activeFormType);
        setEmittedTrackStart(true);
    }
    useEffect(() => setFormIsValid(formDataIsValid(formData, activeFormType)), [formData, activeFormType]);
    return (
        <Modal canClose={!isProcessing} title="Edit payment details" onCancel={cancelModalWithTracking}>
            <Modal.Body>
                <CustomisedModalBody>
                    <CreditCardForm
                        onHeaderClick={() => {
                            setActiveForm(FormType.CreditCard);
                        }}
                        onChange={setFormData}
                        isActive={activeFormType == FormType.CreditCard}
                    />
                    <DirectDebitCardForm
                        onHeaderClick={() => {
                            setActiveForm(FormType.DirectDebit);
                        }}
                        onChange={setFormData}
                        isActive={activeFormType == FormType.DirectDebit}
                    />
                    {isProcessing && <Spinner />}
                </CustomisedModalBody>
            </Modal.Body>
            <Modal.Footer>
                <ButtonRow>
                    <Button disabled={!!isProcessing} type="secondary" onClick={cancelModalWithTracking}>
                        Cancel
                    </Button>
                    <Button disabled={!formIsValid || !!isProcessing} onClick={onClickSaveBtn}>
                        Save
                    </Button>
                </ButtonRow>
            </Modal.Footer>
        </Modal>
    );
};

export default EditModal;
